import React from "react"
import { useSelector } from "react-redux"
import { createHashRouter, Navigate } from "react-router-dom"
import { routes } from "./routes.js"
import PrivateRoutes from "./PrivateRoutes"
import PublicRoutes from "./PublicRoutes"
import PermissionBasedRedirect from "./PermissionBasedRedirect"
import PermissionGuard from "./PermissionGuard"
import { ROUTE_PERMISSIONS } from "./routePermissions"
import { isFeatureEnabled } from "config/features"
import { AI_DASHBOARD_ALLOWED_EMAILS } from "constants/dashboard"

import RouteChangeWatcher from "components/RouteChangeWatcher"
import RouteErrorBoundary from "modules/errors/RouteErrorBoundary"

import ScrollToTopLayout from "layouts/ScrollToTopLayout"
import DeviseLayout from "layouts/DeviseLayout"

// new routes
import { AntdComps } from "pages/AntdComps.js"

// authentication new routes
import { SignIn as SignInV2 } from "pagesV2/auth/SignIn"
import { SignUp as SignUpV2 } from "pagesV2/auth/SignUp"
import { SignUpOptions } from "pagesV2/auth/SignUp/Options"
import { NewPassword } from "pagesV2/auth/NewPassword"
import { ConfirmEmail } from "pagesV2/auth/SignUp/ConfirmEmail"
import { ConfirmPhone } from "pagesV2/auth/SignUp/ConfirmPhone"
import { ConfirmInfo } from "pagesV2/auth/SignUp/ConfirmInfo"
import { PasswordRecovery } from "pagesV2/auth/PasswordRecovery"
import { PasswordRecoveryEnterCode } from "pagesV2/auth/PasswordRecovery/EnterCode"
import { PasswordRecoveryCreatePassword } from "pagesV2/auth/PasswordRecovery/CreatePassword"
import { PasswordRecoverySuccess } from "pagesV2/auth/PasswordRecovery/Success"

// new layouts
import { PersonalInfoLayout } from "layoutsV2/pro/PersonalInfoLayout/PersonalInfoLayout.jsx"
import { ProfileInformation as PersonalInfoProfileInformation } from "ui/PersonalInfo/ProfileInformation"
import { ProfileImages as PersonalProfileInProfileImages } from "ui/PersonalInfo/ProfileImages"
import { PersonalInfoAddresses } from "ui/PersonalInfo/Addresses"
import { PersonalInfoLicenses } from "ui/PersonalInfo/Licenses"
import { PersonalInfoStripe } from "ui/PersonalInfo/ConnectWithStripe"
import { AdventureMode as PersonalInfoAdventureMode } from "ui/PersonalInfo/Mode"

import { guideRoutes } from "./old-router"
import { ProHeader } from "layoutsV2/pro/ProHeader"
import { ProHomeLayout } from "layoutsV2/pro/ProHomeLayout"
import { ProSettingsLayout } from "layoutsV2/pro/ProSettingsLayout"
import { ProSettings } from "pagesV2/pro/Settings/ProSettings"
import { ProLicenses } from "pagesV2/pro/Settings/ProLicenses"
import { ProfileInformation } from "pagesV2/pro/Settings/ProfileInformation"
import { StripeAccount } from "pagesV2/pro/Settings/StripeAccount"
import { LoginAndSecurity } from "pagesV2/pro/Settings/LoginAndSecurity"
import { GoogleCalendar } from "pagesV2/pro/Settings/GoogleCalendar"
import { WebsiteIntegration } from "pagesV2/pro/Settings/WebsiteIntegration"
import { Messages } from "pagesV2/pro/Messages"
import { AdventureMode } from "pagesV2/pro/Settings/AdventureMode"
import GuestLandingLayout from "layoutsV2/client/GuestLandingLayout"
import Calendar from "pagesV2/pro/Calendar/index.jsx"
// old pages
import DashboardPageV2 from "pages/guide/dashboard/DashboardPageV2"

// Guide Booking
import { ProBookingsLayout } from "layoutsV2/pro/ProBookingsLayout"
import GuideBookingsPage from "pagesV2/pro/Bookings"
import BookingDetailsPage from "pagesV2/pro/Bookings/BookingDetailsPage"

import ProClients from "pagesV2/pro/clients/ClientsPage"
import GuideClientWindow from "pagesV2/pro/clients/ClientWindow"
import ClientDetails from "pagesV2/pro/clients/ClientDetails"

import GuideTrips from "pages/guide/trips/TripsPage"
import Trips from "pages/public/trips/TripsPage"
import Trip from "pages/public/trips/TripPage"

import Boats from "pages/guide/boats/BoatsPage"
import BoatWindow from "pages/guide/boats/BoatWindow"

// import MessagesPage from "pages/private/messages/MessagesPage"
import GuideLayout from "layouts/public/GuideLayout"
import GuideAccounting from "pages/guide/accouting/Accounting"
import GuideAccountingExpenseWindow from "pages/guide/accouting/ExpenseWindow"

import GuideMarketing from "pages/guide/marketing/MarketingPage"
import GuideFeeder from "pages/guide/marketing/FeederPage"
import GuidePostWindow from "pages/guide/marketing/PostWindow"
import GuideTemplates from "pages/guide/marketing/TemplatesPage"
import GuideTemplatesParents from "pages/guide/marketing/TemplatesParentsPage"
import GuideTemplate from "pages/guide/marketing/TemplatePage"

import Faq from "pages/FaqPage"
import TermsAndConditions from "pages/TermsAndConditionsPage"
import PrivacyPolicy from "pages/PrivacyPolicyPage"
import { ContactSupportPage } from "pages/ContactSupportPage.js"
import PublicGuide from "pages/public/guides/GuidePage"
import PublicGuideTrips from "pages/public/guides/TripsPage"
import PublicGuideFeeder from "pages/public/guides/FeederPage"
import PublicGuideReviews from "pages/public/guides/ReviewsPage"
import PublicGuideLocation from "pages/public/guides/LocationPage"
import PublicGuideBoats from "pages/public/guides/BoatsPage"
import GuideLayoutReskin from "layouts/public/GuideLayoutReskin.js"

import { ProNewListingLayout } from "layoutsV2/pro/ProNewListingLayout"
import { ListingDetails } from "pagesV2/pro/Experiences/ListingDetails"
import { ListingScheduling } from "pagesV2/pro/Experiences/ListingScheduling"
import { ListingMedia } from "pagesV2/pro/Experiences/ListingMedia"
// import { ListingResources } from "pagesV2/pro/Experiences/ListingResources"
import { ListingLocation } from "pagesV2/pro/Experiences/ListingLocation"
import { ListingPreview } from "pagesV2/pro/Experiences/ListingPreview"

import { ProExperiencesHomeLayout } from "layoutsV2/pro/proExperiencesHomeLayout"
import { ExperiencesHome } from "pagesV2/pro/Experiences/Home"
import Partner from "pagesV2/client/PartnerPage/index.js"
import { ProClientsLayout } from "layoutsV2/pro/proClientsLayout/index.jsx"
import EmbeddedExperienceListing from "pagesV2/pro/Embedded/ExperienceListing/index.jsx"
import EmbeddedExperienceDetail from "pagesV2/pro/Embedded/ExperienceDetail/index.jsx"
import EmbeddedExperienceBooking from "pagesV2/pro/Embedded/ExperienceBooking/index.jsx"
import ResourcesContainer from "pages/guide/resources/index.jsx"
import EquipmentPage from "pages/guide/resources/equipment"
import CrewMembersPage from "pages/guide/resources/crewMembers/CrewMembersPage"
import EquipmentDetails from "pages/guide/resources/equipment/EquipmentDetails"
import EquipmentFormPage from "pagesV2/pro/Equipment/EquipmentFormPage"
import CrewMemberDetailPage from "pages/guide/resources/crewMembers/crewDetail"
// Clients routes
import Referral from "pages/public/referral/ReferralPage"
import Sales from "pages/public/sales/SalesPage"
import PaymentStatusPage from "pages/public/PaymentStatusPage.jsx"
import { ClientMessagesLayout } from "layoutsV2/client/ClientMessagesLayout"
import { ClientMessages } from "pagesV2/client/Messages"
import { ProMessagesLayout } from "layoutsV2/pro/ProMessagesLayout"
import DashboardLayout from "layoutsV2/client/GuestDashboardLayout"
import FavoritesPage from "pages/client/favorites/FavoritesPage"

import ClientBookings from "pages/client/bookings/BookingsPage"
import ClientBookingsBooked from "pages/client/bookings/BookedTripsPage"
import ClientBookingsPast from "pages/client/bookings/PastTripsPage"
import ClientBooking from "pages/client/bookings/BookingTripPage"
import PublicBookingPage from "pages/client/bookings/PublicBookingPage"
import WaiverResumePage from "pages/client/bookings/WaiverResumePage"
import TripBookingWaiverPage from "pages/client/bookings/TripBookingWaiverPage"
import ClientActions from "pagesV2/pro/clients/ClientTable/Modals/ClientActions/index.jsx"
import ClientManageGroup from "pagesV2/pro/clients/ClientTable/Modals/ClientManageGroup/index.jsx"
import StripeWrapper from "components/stripe/StripeWrapper.js"
import CancellationpolicyPage from "pagesV2/client/CancellationPolicyPage/CancellationPolicy.jsx"
import Splash from "pagesV2/client/SplashScreen"
import ImportClientList from "pagesV2/pro/clients/ClientTable/Modals/ImportClientList/index.js"
import FailedImportReport from "pagesV2/pro/clients/ClientTable/Modals/FailedClientList/index.js"
import ViewImportHistory from "pagesV2/pro/clients/ViewImportRecords/index.jsx"
import ConfirmPassword from "pagesV2/client/ConfirmPassword/index.jsx"
import GuestClientBooking from "pagesV2/pro/GuestClientBooking/index.jsx"
import ClientGuestBookingConfirmation from "pages/client/bookings/BookingConfirmation.jsx"
import BookingConfirmationLegacy from "pagesV2/pro/Embedded/BookingConfirmation/index.jsx"
import GuideBookingWindow from "pagesV2/pro/Bookings/BookingWindow/index.jsx"
import { ProCalendarLayout } from "layoutsV2/pro/ProCalendarLayout/index.jsx"
import EmailConfirmation from "pagesV2/client/EmailConfirmation/index.jsx"
import ProGuideProfileLayout from "layoutsV2/pro/ProGuideProfileLayout/index.jsx"
import Landing from "pages/LandingPage.js"
import ClientImportDetail from "pagesV2/pro/clients/ClientImportDetail/index.jsx"
// import ClientProfileLayout from "layoutsV2/client/ClientProfileLayout/index.jsx"
// import ClientProfile from "pagesV2/client/Profile/index.jsx"
import ClientProfileLayout from "layoutsV2/client/ClientProfileLayout/index.jsx"
import ClientProfile from "pagesV2/client/Profile/index.jsx"
import GuestOnly from "./GuestOnly.jsx"
import { ProResourcesLayout } from "layoutsV2/pro/proResourcesLayout/index.jsx"
import PaymentProviders from "pagesV2/pro/Settings/ProSettings/PaymentProviders/index.jsx"
import { ProDigitalWaiverAndContractsLayout } from "layoutsV2/pro/ProDigitalWaiverAndContractsLayout/index.jsx"
import { DigitalWaiverAndContracts } from "pagesV2/pro/DigitalWaiverAndContracts/index.jsx"
import { WaiverWindow } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/WaiverWindow/index.jsx"
import { CustomTemplate } from "pagesV2/pro/Settings/ProSettings/CustomTemplate/index.jsx"
import { TemplateDetails } from "pagesV2/pro/Settings/ProSettings/CustomTemplate/TemplateDetails/index.jsx"
import ListingDigitalWaiver from "pagesV2/pro/Experiences/ListingDigitalWaiver/index.jsx"
import ListingTaxFees from "pagesV2/pro/Experiences/ListingTaxFees/index.jsx"
import SharedWaivers from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/index.jsx"
import WaiverThankYou from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/ThankYou/index.jsx"
import { ProStaffLayout } from "layoutsV2/pro/ProStaffLayout/index.jsx"

//staff
import Staff from "pagesV2/pro/Staff/index.jsx"
import Forbidden403 from "pagesV2/errors/Forbidden403"

import { TaxesAndFees } from "pagesV2/pro/Settings/TaxesAndFees/index.jsx"
import { CalendarExperienceColors } from "pagesV2/pro/Settings/CalendarExperienceColors/index.jsx"
import { PricingPromotions } from "pagesV2/pro/Settings/PricingPromotions/index.jsx"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants.js"
import { NewListingResources } from "pagesV2/pro/Experiences/NewListingResources/index.jsx"
import { AccountManagement } from "pagesV2/pro/Settings/ProSettings/AccountManagement/index.jsx"

const AIDashboardEmailGuard = ({ children }) => {
  const userEmail = useSelector((state) => state.user?.email)
  if (!AI_DASHBOARD_ALLOWED_EMAILS.includes(userEmail)) {
    return <Navigate to={routes.proCalendarPath()} replace />
  }
  return children
}

export const router = createHashRouter([
  { path: routes.paymentSuccess(), element: <PaymentStatusPage status="success" /> },
  { path: routes.paymentFailure(), element: <PaymentStatusPage status="failure" /> },
  { path: routes.forbidden403(), element: <Forbidden403 /> },
  {
    element: (
      <RouteChangeWatcher>
        <ScrollToTopLayout />
      </RouteChangeWatcher>
    ),
    errorElement: (
      <DeviseLayout>
        <RouteErrorBoundary />
      </DeviseLayout>
    ),
    children: [
      // ...oldRouter,
      {
        // Public only routes
        element: <PublicRoutes only />,
        children: [
          {
            element: <AntdComps />,
            path: routes.antd()
          },
          ...(window.location.hostname === "ds.guide"
            ? [
              {
                path: routes.rootPath(),
                element: <Splash />,
                exact: true
              }
            ]
            : []),

          {
            element: <GuestLandingLayout />,
            children: [
              // Use positive condition to avoid negated condition lint warning
              ...(window.location.hostname === "ds.guide"
                ? []
                : [
                  {
                    path: routes.rootPath(),
                    element: <Landing />
                  }
                ]),
              { path: routes.referralPath(), element: <Referral /> },
              { path: routes.salesPath(), element: <Sales /> }
            ]
          },

          // v2 paths start
          { path: routes.signInV2(), element: <SignInV2 /> },
          { path: routes.splashScreen(), element: <Splash /> },
          { path: routes.signUpV2(), element: <SignUpV2 /> },
          { path: routes.newPassword(), element: <NewPassword /> },
          { path: routes.signUpOptions(), element: <SignUpOptions /> },
          { path: routes.confirmEmail(), element: <ConfirmEmail /> },
          { path: routes.confirmPhone(), element: <ConfirmPhone /> },
          { path: routes.confirmInfo(), element: <ConfirmInfo /> },
          { path: routes.passwordRecovery(), element: <PasswordRecovery /> },
          { path: routes.passwordRecoveryEnterCode(), element: <PasswordRecoveryEnterCode /> },
          { path: routes.passwordRecoveryCreatePassword(), element: <PasswordRecoveryCreatePassword /> },
          { path: routes.passwordRecoverySuccess(), element: <PasswordRecoverySuccess /> }

          // v2 paths end
        ]
      },
      {
        element: (
          <StripeWrapper>
            <GuestClientBooking />
          </StripeWrapper>
        ),
        path: routes.bookingWizardPath()
      },
      {
        element: <BookingConfirmationLegacy mode={"clientBooking"} />,
        path: routes.bookingConfirmationLegacy()
      },
      {
        // Public routes
        element: <PublicRoutes />,
        children: [
          {
            element: <GuestLandingLayout />,
            children: [
              // { path: oldRoutes.becomeAGuidePath(), element: <GuideLanding /> },
              { path: routes.faqPath(), element: <Faq /> },
              { path: routes.termsAndConditionsPath(), element: <TermsAndConditions /> },
              { path: routes.privacyPolicyPath(), element: <PrivacyPolicy /> },
              { path: routes.tripsPath(), element: <Trips /> },
              { path: routes.tripPath(), element: <Trip /> }
            ]
          },
          {
            element: <GuideLayout />,
            path: routes.publicGuidePath(),
            children: [
              { index: true, element: <PublicGuide /> },
              { path: routes.publicGuideTripsPath(), element: <PublicGuideTrips /> },
              { path: routes.publicGuidePostsPath(), element: <PublicGuideFeeder /> },
              { path: routes.publicGuideReviewsPath(), element: <PublicGuideReviews /> },
              { path: routes.publicGuideLocationPath(), element: <PublicGuideLocation /> },
              { path: routes.publicGuideBoatsPath(), element: <PublicGuideBoats /> }
            ]
          },
          {
            element: <ProGuideProfileLayout />,
            path: routes.proGuideProfilePath()
          },
          {
            element: (
              <StripeWrapper>
                <EmbeddedExperienceBooking mode={BOOKING_MODES.guideProfile} />
              </StripeWrapper>
            ),
            path: routes.guideProfileExperiencesBookingPath()
          },
          { path: routes.ClientEmailActionPath(), element: <ClientActions contactType="email" /> },
          { path: routes.ClientTextActionPath(), element: <ClientActions contactType="text" /> },
          { path: routes.ClientMessageActionPath(), element: <ClientActions contactType="message" /> },
          { path: routes.clientSharedWaivers(), element: <SharedWaivers /> },
          { path: routes.clientTripBookingSharedWaiverPath(), element: <SharedWaivers /> },
          { path: routes.clientWaiverThankYou(), element: <WaiverThankYou /> },
          { path: routes.clientBookingPath(), element: <ClientBooking /> },
          { path: routes.publicBookingPath(), element: <PublicBookingPage /> },
          { path: routes.clientTripBookingWaiverPath(), element: <TripBookingWaiverPage /> },
          { path: routes.clientBookingWaiverResumePath(), element: <WaiverResumePage /> }
        ]
      },
      { path: routes.partner(), element: <Partner /> },
      // V2 Clients routes
      { path: routes.confirmPassword(), element: <ConfirmPassword /> },
      {
        path: routes.setNewPasswordForGuest(),
        element: (
          <GuestOnly>
            <ConfirmPassword isGuest />
          </GuestOnly>
        )
      },
      {
        path: routes.clientRootPath(),
        element: <PrivateRoutes accessRole="user" />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true, element: <Navigate to={routes.tripsPath()} /> },
              // { path: routes.clientMessagesPath(), element: <ClientMessages /> },
              {
                element: <ClientMessagesLayout />,
                path: routes.clientMessagesPath(),
                children: [{ index: true, element: <ClientMessages /> }]
              },

              { path: routes.clientFavoritesPath(), element: <FavoritesPage /> },
              {
                path: routes.clientBookingsPath(),
                element: <ClientBookings />,
                children: [
                  { index: true, element: <ClientBookingsBooked /> },
                  { path: routes.clientBookingsPastTripsPath(), element: <ClientBookingsPast /> }
                ]
              },
              { path: routes.clientBookingPath(), element: <ClientBooking /> },
              {
                element: <ClientProfileLayout />,
                children: [{ path: routes.clientSettingsRootPath(), element: <ClientProfile /> }]
              }
            ]
          },
          {
            element: (
              <StripeWrapper>
                <GuestClientBooking />
              </StripeWrapper>
            ),
            path: routes.clientBookingWizardRootPath()
          },
          {
            element: <ClientGuestBookingConfirmation />,
            path: routes.clientBookingConfirmation()
          }
        ]
      },
      {
        element: <ClientGuestBookingConfirmation />,
        path: routes.bookingConfirmation()
      },

      // V2 Guide routes
      {
        path: routes.proRootPath(),
        element: <PrivateRoutes accessRoles={["guide_user", "staff_user"]} />,
        children: [
          {
            path: routes.proRootPath(),
            element: <ProHeader />,
            children: [
              {
                path: routes.proHomeRootPath(),
                element: <ProHomeLayout />,
                children: [
                  {
                    index: true,
                    element: <PermissionBasedRedirect />
                  },
                  {
                    path: routes.proSettingsRootPath(),
                    element: <ProSettingsLayout />,
                    children: [
                      { index: true, element: <ProSettings /> },
                      { path: routes.proSettingsStripeAccount(), element: <StripeAccount /> },
                      {
                        path: routes.proSettingsPaymentProviders(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <PaymentProviders />
                          </PermissionGuard>
                        )
                      },
                      { path: routes.proSettingsProfileInformation(), element: <ProfileInformation /> },
                      { path: routes.proSettingsAdventureMode(), element: <AdventureMode /> },
                      {
                        path: routes.proSettingsLicenses(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <ProLicenses />
                          </PermissionGuard>
                        )
                      },
                      { path: routes.proSettingsLoginAndSecurity(), element: <LoginAndSecurity /> },
                      {
                        path: routes.proSettingsGoogleCalendar(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <GoogleCalendar />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: routes.proSettingsWebsiteIntegration(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <WebsiteIntegration />
                          </PermissionGuard>
                        )
                      },
                      { path: routes.proSettingsAccountManagement(), element: <AccountManagement /> },
                      { path: routes.proSettingsTaxesAndFees(), element: <TaxesAndFees /> },
                      { path: routes.proSettingsCalendarExperienceColors(), element: <CalendarExperienceColors /> },
                      { path: routes.proSettingsPricingPromotions(), element: <PricingPromotions /> },
                      {
                        path: routes.proSettingsCustomTemplate(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <CustomTemplate />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: routes.proSettingsTemplateDetails(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <TemplateDetails />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: routes.proSettingsEditTemplateDetails(":id"),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.SETTINGS}>
                            <TemplateDetails />
                          </PermissionGuard>
                        )
                      }
                    ]
                  },
                  {
                    path: routes.proNewListingRootPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.EXPERIENCES}>
                        <ProNewListingLayout />
                      </PermissionGuard>
                    ),
                    children: [
                      { index: true, element: <Navigate to={routes.proNewListingDetailsPath()} /> },
                      { path: routes.proNewListingDetailsPath(), element: <ListingDetails /> },
                      { path: routes.proNewListingSchedulingPath(), element: <ListingScheduling /> },
                      { path: routes.proNewListingMediaPath(), element: <ListingMedia /> },
                      // { path: routes.proNewListingResourcesPath(), element: <ListingResources /> },
                      { path: routes.proNewListingResourcesPath(), element: <NewListingResources /> },

                      { path: routes.proNewListingLocationPath(), element: <ListingLocation /> },
                      { path: routes.proNewListingDigitalWaiverPath(), element: <ListingDigitalWaiver /> },
                      { path: routes.proNewListingTaxFeesPath(), element: <ListingTaxFees /> },
                      { path: routes.proNewListingPreviewPath(), element: <ListingPreview /> }
                    ]
                  },
                  {
                    path: routes.proExperiencesPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.EXPERIENCES}>
                        <ProExperiencesHomeLayout />
                      </PermissionGuard>
                    ),
                    children: [{ index: true, element: <ExperiencesHome /> }]
                  },
                  {
                    path: routes.guideResourcesPath(),
                    element: <ProResourcesLayout />,
                    children: [
                      { index: true, element: <ResourcesContainer /> },
                      {
                        path: routes.guideResourcesEquipmentPath(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.EQUIPMENT}>
                            <ResourcesContainer />
                          </PermissionGuard>
                        ),
                        children: [{ index: true, element: <EquipmentPage /> }]
                      },
                      {
                        path: routes.guideResourcesCrewPath(),
                        element: <ResourcesContainer />,
                        children: [
                          { index: true, element: <CrewMembersPage /> },
                          { path: "edit/:id", element: <CrewMembersPage /> }
                        ]
                      },
                      {
                        path: routes.guideEquipmentDetailsPath(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.EQUIPMENT}>
                            <EquipmentDetails />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: routes.guideEquipmentAddPath(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.EQUIPMENT}>
                            <EquipmentFormPage />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: routes.guideEquipmentEditPath(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.EQUIPMENT}>
                            <EquipmentFormPage />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: routes.guideEquipmentDuplicatePath(),
                        element: (
                          <PermissionGuard {...ROUTE_PERMISSIONS.EQUIPMENT}>
                            <EquipmentFormPage />
                          </PermissionGuard>
                        )
                      },
                      {
                        path: "/pro/home/resources/crew/:id",
                        element: <CrewMemberDetailPage context="crew" />,
                        children: [
                          { path: routes.guideCrewMemberDetailsPath(), element: null },
                          { path: routes.guideCrewMemberCalendarPath(), element: null },
                          { path: routes.guideCrewMemberEditPath(), element: null }
                        ]
                      }
                    ]
                  },
                  {
                    path: routes.proEditExperiencePath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.EXPERIENCES}>
                        <ProNewListingLayout />
                      </PermissionGuard>
                    ),
                    children: [
                      { index: true, element: <Navigate to={routes.proEditExperienceDetailsPath()} /> },
                      { path: routes.proEditExperienceDetailsPath(), element: <ListingDetails /> },
                      { path: routes.proEditExperienceSchedulingPath(), element: <ListingScheduling /> },
                      { path: routes.proEditExperienceMediaPath(), element: <ListingMedia /> },
                      // { path: routes.proEditExperienceResourcesPath(), element: <ListingResources /> },
                      { path: routes.proEditExperienceResourcesPath(), element: <NewListingResources /> },
                      { path: routes.proEditExperienceLocationPath(), element: <ListingLocation /> },
                      { path: routes.proEditExperienceDigitalWaiverPath(), element: <ListingDigitalWaiver /> },
                      { path: routes.proEditExperienceTaxFeesPath(), element: <ListingTaxFees /> },
                      { path: routes.proEditExperiencePreviewPath(), element: <ListingPreview /> }
                    ]
                  },
                  ...(isFeatureEnabled("AI_DASHBOARD")
                    ? [
                        {
                          path: routes.guideAIDashboardPath(),
                          element: <AIDashboardEmailGuard><DashboardPageV2 /></AIDashboardEmailGuard>
                        }
                      ]
                    : []),
                  {
                    path: routes.proCalendarPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.CALENDAR}>
                        <StripeWrapper>
                          <ProCalendarLayout />
                        </StripeWrapper>
                      </PermissionGuard>
                    ),
                    children: [
                      {
                        index: true,
                        element: <Calendar />
                      }
                    ]
                  },
                  {
                    path: routes.guideBookingsPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.BOOKINGS}>
                        <ProBookingsLayout />
                      </PermissionGuard>
                    ),
                    children: [
                      { index: true, element: <GuideBookingsPage /> },
                      {
                        path: routes.guideBookingDetailsPath(),
                        element: (
                          <StripeWrapper>
                            <BookingDetailsPage />
                          </StripeWrapper>
                        )
                      },
                      {
                        path: routes.guideBookingNewPath(),
                        element: (
                          <StripeWrapper>
                            <GuideBookingWindow />
                          </StripeWrapper>
                        )
                      },
                      // Conditionally include Edit Booking route based on feature flag
                      ...(isFeatureEnabled("EDIT_BOOKING")
                        ? [
                          {
                            path: routes.guideBookingEditPath(),
                            element: (
                              <StripeWrapper>
                                <GuideBookingWindow />
                              </StripeWrapper>
                            )
                          }
                        ]
                        : []),
                      {
                        path: routes.guideBookingBookAgainPath(),
                        element: (
                          <StripeWrapper>
                            <GuideBookingWindow />
                          </StripeWrapper>
                        )
                      }
                    ]
                  },
                  {
                    path: routes.guideClientsPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.CLIENTS}>
                        <ProClientsLayout />
                      </PermissionGuard>
                    ),
                    children: [
                      {
                        index: true,
                        element: <ProClients />
                      },
                      {
                        path: routes.guideClientPath(),
                        element: (
                          <StripeWrapper>
                            <ClientDetails />
                          </StripeWrapper>
                        )
                      },
                      { path: routes.guideClientNewPath(), element: <GuideClientWindow /> },
                      { path: routes.guideClientPath(), element: <GuideClientWindow /> },
                      { path: routes.guideClientEditPath(), element: <GuideClientWindow /> },
                      { path: routes.guideImportClientsListPath(), element: <ImportClientList /> },
                      { path: routes.guideFailedClientsListPath(), element: <FailedImportReport /> },
                      { path: routes.guideImportClientHistory(), element: <ViewImportHistory /> },
                      { path: routes.guideImportClientDetail(), element: <ClientImportDetail /> },
                      { path: routes.guideClientManageGroup(), element: <ClientManageGroup /> }
                    ]
                  },
                  { path: routes.guideClientEmailActionPath(), element: <ClientActions contactType="email" /> },
                  { path: routes.guideClientTextActionPath(), element: <ClientActions contactType="text" /> },
                  {
                    path: routes.guideClientMessageActionPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.MESSAGES}>
                        <ClientActions contactType="message" />
                      </PermissionGuard>
                    )
                  },
                  {
                    path: routes.proStaffPath(),
                    element: <ProStaffLayout />,
                    children: [
                      {
                        index: true,
                        element: <Staff />
                      },
                      {
                        path: routes.proStaffDetailPath(),
                        element: <CrewMemberDetailPage context="staff" />,
                        children: [{ path: "edit", element: null }]
                      }
                    ]
                  },
                  {
                    path: routes.ProDigitalWaiverAndContracts(),
                    element: <ProDigitalWaiverAndContractsLayout />,
                    children: [
                      { index: true, element: <DigitalWaiverAndContracts /> },
                      { path: "new", element: <WaiverWindow /> },
                      { path: ":id/edit", element: <WaiverWindow /> },
                      { path: ":id/view", element: <WaiverWindow /> }
                    ]
                  },

                  { path: routes.guideTripsPath(), element: <GuideTrips /> },
                  { path: routes.guideTripPath(), element: <Trip guideView /> },
                  {
                    path: routes.guideBoatsPath(),
                    element: <Boats />,
                    children: [
                      { path: routes.guideAddNewBoatPath(), element: <BoatWindow /> },
                      { path: routes.guideEditBoatPath(), element: <BoatWindow /> }
                    ]
                  },
                  {
                    path: routes.proMessagesRootPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.MESSAGES}>
                        <ProMessagesLayout />
                      </PermissionGuard>
                    ),
                    children: [{ index: true, element: <Messages /> }]
                  },
                  {
                    path: routes.guideAccountingPath(),
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.ACCOUNTING}>
                        <GuideAccounting />
                      </PermissionGuard>
                    ),
                    children: [
                      { path: routes.guideAccountingAddExpancePath(), element: <GuideAccountingExpenseWindow /> },
                      { path: routes.guideAccountingEditExpancePath(), element: <GuideAccountingExpenseWindow /> }
                    ]
                  },
                  {
                    element: (
                      <PermissionGuard {...ROUTE_PERMISSIONS.MARKETING}>
                        <GuideMarketing />
                      </PermissionGuard>
                    ),
                    children: [
                      {
                        path: routes.guideMarketingPath(),
                        element: <GuideFeeder />,
                        children: [
                          { path: routes.guideMarketingPostNewPath(), element: <GuidePostWindow /> },
                          { path: routes.guideMarketingPostEditPath(), element: <GuidePostWindow /> }
                        ]
                      },
                      {
                        path: routes.guideMarketingTemplatesPath(),
                        element: <GuideTemplates />,
                        children: [
                          {
                            path: routes.guideMarketingTemplatesParentsPath(),
                            element: <GuideTemplatesParents />
                          },
                          { path: routes.guideMarketingTemplateNewPath(), element: <GuideTemplate /> },
                          { path: routes.guideMarketingTemplateEditPath(), element: <GuideTemplate /> }
                        ]
                      }
                    ]
                  },
                  {
                    path: routes.guideFaqPath(),
                    element: <Faq />
                  },
                  {
                    path: routes.contactSupportPath(),
                    element: <ContactSupportPage />
                  },
                  {
                    element: <GuideLayoutReskin />,
                    path: routes.publicGuidePath(),
                    children: [
                      { index: true, element: <PublicGuide /> },
                      { path: routes.publicGuideTripsPath(), element: <PublicGuideTrips /> },
                      { path: routes.publicGuidePostsPath(), element: <PublicGuideFeeder /> },
                      { path: routes.publicGuideReviewsPath(), element: <PublicGuideReviews /> },
                      { path: routes.publicGuideLocationPath(), element: <PublicGuideLocation /> },
                      { path: routes.publicGuideBoatsPath(), element: <PublicGuideBoats /> }
                    ]
                  }
                ]
              },
              {
                path: routes.proPersonalInfoRootPath(),
                element: <PersonalInfoLayout />,
                children: [
                  { index: true, element: <Navigate to={routes.proPersonalInfoProfileInfo()} /> },
                  { path: routes.proPersonalInfoProfileInfo(), element: <PersonalInfoProfileInformation /> },
                  { path: routes.proPersonalInfoProfileImages(), element: <PersonalProfileInProfileImages /> },
                  { path: routes.proPersonalInfoAddressesPath(), element: <PersonalInfoAddresses /> },
                  { path: routes.proPersonalInfAdventureMode(), element: <PersonalInfoAdventureMode /> },
                  { path: routes.proPersonalInfoLicenses(), element: <PersonalInfoLicenses /> },
                  { path: routes.proPersonalInfoPayment(), element: <PersonalInfoStripe /> }
                ]
              },
              // add here old routes
              ...guideRoutes
            ]
          }
        ]
      },
      { path: routes.emailConfirmationPath(), element: <EmailConfirmation /> },
      {
        element: <EmbeddedExperienceListing />,
        path: routes.embeddedExperienceListing()
      },
      {
        element: <EmbeddedExperienceDetail mode={BOOKING_MODES.embedded} />,
        path: routes.embeddedExperienceDetail()
      },
      {
        element: (
          <StripeWrapper>
            <EmbeddedExperienceBooking mode={BOOKING_MODES.embedded} />
          </StripeWrapper>
        ),
        path: routes.embeddedExperienceBooking()
      },
      {
        element: <ClientGuestBookingConfirmation />,
        path: routes.embeddedExperienceBookingConfirmation()
      },
      { path: routes.cancellationpolicy(), element: <CancellationpolicyPage /> }
    ]
  }
])
